import React, { useEffect, useRef, useState } from "react";
import {
  Mic, MicOff, Video, VideoOff, X, Maximize2, Minimize2,
  MonitorUp, PhoneOff, UserPlus,
} from "lucide-react";
import { toast } from "sonner";

const JITSI_DOMAIN = "meet.qedara-consulting.uk";
let externalApiPromise;

function loadExternalApi() {
  if (window.JitsiMeetExternalAPI) return Promise.resolve();
  if (!externalApiPromise) {
    externalApiPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="https://${JITSI_DOMAIN}/external_api.js"]`);
      const script = existing || document.createElement("script");
      const onLoad = () => window.JitsiMeetExternalAPI
        ? resolve()
        : reject(new Error("API-ul Jitsi nu a pornit"));
      script.addEventListener("load", onLoad, { once: true });
      script.addEventListener("error", () => reject(new Error("API-ul Jitsi nu s-a încărcat")), { once: true });
      if (!existing) {
        script.src = `https://${JITSI_DOMAIN}/external_api.js`;
        script.async = true;
        document.head.appendChild(script);
      }
    });
  }
  return externalApiPromise;
}

export default function CallPanel({ roomName, token, channelName, currentUser, users = [], onInvite, onClose }) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [joined, setJoined] = useState(false);
  const [audioMuted, setAudioMuted] = useState(false);
  const [videoMuted, setVideoMuted] = useState(false);
  const [sharingScreen, setSharingScreen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [selectedInvitees, setSelectedInvitees] = useState(new Set());
  const meetRef = useRef(null);
  const apiRef = useRef(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    let disposed = false;
    let api;
    setLoading(true);
    setError("");
    setJoined(false);

    loadExternalApi()
      .then(() => {
        if (disposed || !meetRef.current) return;
        api = new window.JitsiMeetExternalAPI(JITSI_DOMAIN, {
          roomName,
          parentNode: meetRef.current,
          width: "100%",
          height: "100%",
          jwt: token,
          userInfo: {
            displayName: currentUser?.name,
            email: currentUser?.email,
          },
          configOverwrite: {
            disableDeepLinking: true,
            prejoinConfig: { enabled: false },
          },
          onload: () => setLoading(false),
        });
        apiRef.current = api;
        api.addListener("videoConferenceJoined", () => {
          setLoading(false);
          setJoined(true);
        });
        api.addListener("audioMuteStatusChanged", ({ muted }) => setAudioMuted(muted));
        api.addListener("videoMuteStatusChanged", ({ muted }) => setVideoMuted(muted));
        api.addListener("screenSharingStatusChanged", ({ on }) => setSharingScreen(on));
        api.addListener("errorOccurred", (event) => {
          if (event?.isFatal) setError(event.message || event.name || "Conexiunea pentru apelul video a eșuat");
        });
        api.addListener("readyToClose", () => onCloseRef.current?.());
        // "End call for all" ends the conference from inside the iframe, so it
        // misses the hangup fallback in endCall(); and with enableClosePage=false
        // on our deployment there is no close page left to emit readyToClose.
        // Leaving the conference is the event that arrives on every exit path.
        api.addListener("videoConferenceLeft", () => onCloseRef.current?.());
      })
      .catch((err) => {
        if (!disposed) {
          setLoading(false);
          setError(err.message || "Apelul video nu a pornit");
        }
      });

    return () => {
      disposed = true;
      apiRef.current = null;
      api?.dispose();
    };
  }, [roomName, token, currentUser?.name, currentUser?.email]);

  const execute = (command) => {
    if (!joined || !apiRef.current) return;
    apiRef.current.executeCommand(command);
  };

  const endCall = () => {
    if (!apiRef.current) {
      onCloseRef.current?.();
      return;
    }
    apiRef.current.executeCommand("hangup");
    window.setTimeout(() => onCloseRef.current?.(), 1500);
  };

  const toggleInvitee = (userId) => {
    setSelectedInvitees((current) => {
      const next = new Set(current);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const sendInvites = async () => {
    if (!selectedInvitees.size || !onInvite) return;
    setInviteBusy(true);
    try {
      await onInvite(Array.from(selectedInvitees));
      setSelectedInvitees(new Set());
      setInviteOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invitațiile nu au putut fi trimise");
    } finally {
      setInviteBusy(false);
    }
  };

  return (
    <div
      className={expanded
        ? "fixed inset-0 z-50 bg-bg"
        : "fixed inset-0 z-50 flex flex-col bg-bg xl:static xl:inset-auto xl:z-auto xl:w-[420px] xl:border-l xl:border-brand/30"}
      data-testid={expanded ? "call-fullscreen" : "call-docked"}
    >
      <div className="flex flex-col h-full bg-bg relative" data-testid="call-panel">
        <div className="h-14 px-4 flex items-center justify-between border-b border-brand/30 bg-surface">
          <div className="flex items-center gap-2">
            <Video className="w-4 h-4 text-brand" />
            <div>
              <h3 className="font-bold tracking-tight text-fg">APEL</h3>
              <p className="text-[10px] text-fg/40 mono truncate max-w-[200px]">#{channelName}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              data-testid="call-expand-button"
              onClick={() => setExpanded((value) => !value)}
              className="hidden xl:block p-1.5 text-fg/40 hover:text-fg"
              title={expanded ? "Restrânge la panou" : "Extinde pe tot ecranul"}
              aria-label={expanded ? "Restrânge panoul apelului" : "Extinde panoul apelului"}
            >
              {expanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
            <button data-testid="call-close-button" onClick={endCall} className="p-1.5 text-fg/40 hover:text-red-400" title="Încheie apelul" aria-label="Încheie apelul">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 relative min-h-0">
          {loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-fg/50 text-sm bg-bg z-10" data-testid="call-loading">
              <span className="w-2 h-2 bg-brand rounded-full pulse-amber mb-3" />
              <p className="label-tiny text-brand">// SE CONECTEAZĂ…</p>
            </div>
          )}
          {error && (
            <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-red-400 text-sm bg-bg z-10" data-testid="call-error">
              {error}
            </div>
          )}
          <div ref={meetRef} data-testid="call-iframe-container" className="w-full h-full" />
        </div>

        <div className="h-14 px-3 flex items-center justify-center gap-2 border-t border-brand/30 bg-surface">
          <button
            type="button"
            onClick={() => execute("toggleAudio")}
            disabled={!joined}
            className={`p-2.5 border disabled:opacity-40 ${audioMuted ? "border-red-400/40 text-red-400" : "border-line/20 text-fg/70 hover:text-fg"}`}
            title={audioMuted ? "Pornește microfonul" : "Oprește microfonul"}
            aria-label={audioMuted ? "Pornește microfonul" : "Oprește microfonul"}
            data-testid="call-toggle-audio"
          >
            {audioMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </button>
          <button
            type="button"
            onClick={() => execute("toggleVideo")}
            disabled={!joined}
            className={`p-2.5 border disabled:opacity-40 ${videoMuted ? "border-red-400/40 text-red-400" : "border-line/20 text-fg/70 hover:text-fg"}`}
            title={videoMuted ? "Pornește camera" : "Oprește camera"}
            aria-label={videoMuted ? "Pornește camera" : "Oprește camera"}
            data-testid="call-toggle-video"
          >
            {videoMuted ? <VideoOff className="w-4 h-4" /> : <Video className="w-4 h-4" />}
          </button>
          <button
            type="button"
            onClick={() => execute("toggleShareScreen")}
            disabled={!joined}
            className={`px-3 py-2.5 border text-xs inline-flex items-center gap-1.5 disabled:opacity-40 ${sharingScreen ? "border-brand text-brand bg-brand/10" : "border-line/20 text-fg/70 hover:text-fg"}`}
            title={sharingScreen ? "Oprește partajarea ecranului" : "Partajează ecranul"}
            aria-label={sharingScreen ? "Oprește partajarea ecranului" : "Partajează ecranul"}
            data-testid="call-share-screen"
          >
            <MonitorUp className="w-4 h-4" />
            {sharingScreen ? "Oprește partajarea" : "Partajează ecranul"}
          </button>
          <button
            type="button"
            onClick={() => setInviteOpen((value) => !value)}
            disabled={!joined}
            className={`p-2.5 border disabled:opacity-40 ${inviteOpen ? "border-brand text-brand bg-brand/10" : "border-line/20 text-fg/70 hover:text-fg"}`}
            title="Invită oameni"
            aria-label="Invită oameni în apel"
            data-testid="call-invite-people"
          >
            <UserPlus className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={endCall}
            className="p-2.5 border border-red-400/40 text-red-400 hover:bg-red-400/10"
            title="Încheie apelul"
            aria-label="Încheie apelul"
            data-testid="call-hangup"
          >
            <PhoneOff className="w-4 h-4" />
          </button>
        </div>

        {inviteOpen && (
          <div className="absolute bottom-14 left-3 right-3 z-30 bg-surface border border-line/20 shadow-xl" data-testid="call-invite-panel">
            <div className="px-3 py-2.5 border-b border-line/10 flex items-center justify-between">
              <div>
                <p className="label-tiny">// INVITAȚIE ÎN APEL</p>
                <p className="text-xs text-fg/50 mt-0.5">Selectează membrii pe care vrei să-i inviți.</p>
              </div>
              <button type="button" onClick={() => setInviteOpen(false)} className="text-fg/40 hover:text-fg" aria-label="Închide panoul de invitații">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="max-h-56 overflow-y-auto p-2 space-y-1">
              {users.length === 0 ? (
                <p className="text-xs text-fg/40 p-2">Nu există alți membri disponibili.</p>
              ) : users.map((member) => (
                <label key={member.id} className="flex items-center gap-2 px-2 py-2 hover:bg-fg/5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedInvitees.has(member.id)}
                    onChange={() => toggleInvitee(member.id)}
                    className="accent-current"
                  />
                  <span className="text-sm truncate">{member.name}</span>
                  <span className="text-[10px] text-fg/40 mono truncate ml-auto">{member.email}</span>
                </label>
              ))}
            </div>
            <div className="px-3 py-2.5 border-t border-line/10 flex justify-end">
              <button
                type="button"
                onClick={sendInvites}
                disabled={!selectedInvitees.size || inviteBusy}
                className="btn-primary text-xs px-3 py-1.5 disabled:opacity-40"
                data-testid="call-send-invites"
              >
                {inviteBusy ? "Se trimit…" : `Invită (${selectedInvitees.size})`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
